import { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Select, Space } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { SectionHeader, space } from '@viq-admin/ui';
import { TemplateImport } from './TemplateImport';
// `./templateImportSuggestions`, NOT `./templateImport` — see that file's
// header comment (case-insensitive-filesystem collision with `./TemplateImport`,
// which is what this exact import broke before the rename).
import { suggestionsToMap, type TemplateSuggestion } from './templateImportSuggestions';
import {
  RESERVED_PROPERTY_TARGETS,
  DERIVED_SOURCE_OPTIONS,
  derivedSourceLabel,
  PropertyPicker,
  type PropertyPickerProps,
} from './hubspotShared';
import type {
  HubSpotContactProperty,
  HubSpotDerivedSource,
  HubSpotDescriptionSource,
  HubSpotPropertyMapWire,
} from './types';

/** Empty-picker placeholder AND the read view's no-map fallback (`ConnectionDetail`'s
 * `HubSpotMappingReadView` imports this so the two surfaces never drift apart). */
export const HUBSPOT_SUMMARY_DEFAULT_LABEL = 'VendoIQ AI Summary (default)';

// `RESERVED_PROPERTY_TARGETS`, `DERIVED_SOURCE_OPTIONS`, `derivedSourceLabel`,
// and `PropertyPicker` now live in `./hubspotShared` (a leaf module Task 9's
// `TemplateImport.tsx`/`templateImport.ts` import directly, instead of
// reaching back into this file — see that file's header comment for why: a
// two-way `HubSpotMappingForm.tsx` <-> `TemplateImport.tsx` import broke
// `<TemplateImport>` at render time). Re-exported here unchanged so every
// EXISTING import of them from `./HubSpotMappingForm` (`ConnectionDetail.tsx`,
// this file's own `.test.tsx`) keeps working without a single call-site edit.
export { RESERVED_PROPERTY_TARGETS, DERIVED_SOURCE_OPTIONS, derivedSourceLabel, PropertyPicker };
export type { PropertyPickerProps };

/** Legal ONLY as the `description` (AI summary) target — mirrors the
 * backend's separate `n === 'vendoiq_summary'` checks in `checkName`. Also
 * the property the read view names when notes mode has no custom target. */
export const HUBSPOT_SUMMARY_DEFAULT_PROPERTY = 'vendoiq_summary';

interface StaticRowValue {
  property?: string;
  value?: string;
}

interface DerivedRowValue {
  property?: string;
  source?: HubSpotDerivedSource;
}

interface FormShape {
  description?: string;
  descriptionSource?: HubSpotDescriptionSource;
  staticRows?: StaticRowValue[];
  derivedRows?: DerivedRowValue[];
}

export interface HubSpotMappingFormProps {
  initial?: HubSpotPropertyMapWire;
  /** Loaded by the caller (console edit view) via api.listHubSpotProperties;
   * null = load failed → free-text degrade. */
  properties: HubSpotContactProperty[] | null;
  onSubmit: (map: HubSpotPropertyMapWire) => Promise<void>;
  submitting?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

function toFormShape(initial: HubSpotPropertyMapWire | undefined): FormShape {
  return {
    description: initial?.description,
    descriptionSource: initial?.descriptionSource ?? 'summary',
    staticRows: Object.entries(initial?.static ?? {}).map(([property, value]) => ({
      property,
      value,
    })),
    derivedRows: Object.entries(initial?.derived ?? {}).map(([property, source]) => ({
      property,
      source,
    })),
  };
}

function toWire(values: FormShape): HubSpotPropertyMapWire {
  const map: HubSpotPropertyMapWire = {};

  const description = values.description?.trim();
  if (description) map.description = description;

  // Never emit 'summary': absent is the default, which keeps existing
  // payloads byte-identical.
  if (values.descriptionSource === 'notes') map.descriptionSource = 'notes';

  // `.property` is trimmed here, same as `description` above — `validateMap`
  // (below) already trims before checking a name against
  // `RESERVED_PROPERTY_TARGETS`/duplicates, so a padded name (pasted from a
  // CSV template, or hand-typed in free-text degrade mode) passes the client
  // mirror clean. Without trimming HERE too, the WIRE payload would still
  // carry the raw padded name, and the backend's own `checkName` rejects it
  // ("no surrounding whitespace") — a 422 the operator can't see the cause
  // of, since the client-side check just told them the name was fine.
  const staticEntries = (values.staticRows ?? [])
    .map((r) => ({ property: r?.property?.trim(), value: r?.value }))
    .filter(
      (r): r is { property: string; value: string } =>
        !!r.property && r.value !== undefined && r.value !== '',
    );
  if (staticEntries.length > 0) {
    map.static = Object.fromEntries(staticEntries.map((r) => [r.property, r.value]));
  }

  const derivedEntries = (values.derivedRows ?? [])
    .map((r) => ({ property: r?.property?.trim(), source: r?.source }))
    .filter(
      (r): r is { property: string; source: HubSpotDerivedSource } => !!r.property && !!r.source,
    );
  if (derivedEntries.length > 0) {
    map.derived = Object.fromEntries(derivedEntries.map((r) => [r.property, r.source]));
  }

  return map;
}

type IssueSection = 'description' | 'static' | 'derived';

interface ValidationIssue {
  section: IssueSection;
  index?: number;
  message: string;
}

/**
 * Client-side mirror of the backend's `checkName` + cross-section checks
 * (propertyMap.ts's `superRefine`) — NOT a substitute for it (caps, per-value
 * length, and the enum vocabulary are still backend-only), just enough to
 * catch the two mistakes an operator is most likely to make (typing a
 * reserved/native field name, mapping the same property twice) before a
 * Save round-trips to a 422.
 */
function validateMap(values: FormShape): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seen = new Map<string, Array<{ section: IssueSection; index?: number }>>();

  function checkName(name: string | undefined, section: IssueSection, index?: number) {
    const trimmed = name?.trim();
    if (!trimmed) return;
    if (trimmed === HUBSPOT_SUMMARY_DEFAULT_PROPERTY) {
      if (section !== 'description') {
        issues.push({
          section,
          index,
          message: `"${trimmed}" may only be mapped as the AI summary target`,
        });
      }
    } else if (RESERVED_PROPERTY_TARGETS.has(trimmed)) {
      issues.push({
        section,
        index,
        message: `"${trimmed}" is reserved — natively synced or adapter-owned`,
      });
    }
    const occurrences = seen.get(trimmed) ?? [];
    occurrences.push({ section, index });
    seen.set(trimmed, occurrences);
  }

  checkName(values.description, 'description');
  (values.staticRows ?? []).forEach((r, i) => checkName(r?.property, 'static', i));
  (values.derivedRows ?? []).forEach((r, i) => checkName(r?.property, 'derived', i));

  for (const [name, occurrences] of seen) {
    if (occurrences.length < 2) continue;
    for (const occ of occurrences) {
      issues.push({
        section: occ.section,
        index: occ.index,
        message: `"${name}" is mapped in more than one section`,
      });
    }
  }

  return issues;
}

function issueMessage(
  issues: ValidationIssue[],
  section: IssueSection,
  index?: number,
): string | undefined {
  return issues.find((i) => i.section === section && i.index === index)?.message;
}

/**
 * Operator-facing HubSpot `propertyMap` editor (spec §5.2). Three sections
 * mirroring `PropertyMapSchema` (viq-backend shared/src/crm/propertyMap.ts):
 * the AI-summary redirect (`description`), static literals, and derived
 * bindings — create-only fields the adapter stamps on a new contact.
 *
 * `onDirtyChange` mirrors `SalesforceConnectionForm`'s documented contract
 * (packages/screens/src/integrations/SalesforceConnectionForm.tsx): fires
 * `false` once on mount (a fresh mount is always pristine — same remount-on-
 * every-Edit-click precondition that form's docblock explains), then
 * `form.isFieldsTouched()` on every value change, and `false` again ONLY
 * after a SUCCESSFUL submit. `onSubmit` rejecting (the caller's own save
 * failed) is caught here and swallowed — the caller already owns its own
 * error messaging — specifically so dirty is NOT cleared on a failed save.
 *
 * Row state lives in a plain `values` React state, kept in lock-step with
 * the antd `Form` via `onValuesChange`'s own `allValues` argument (not
 * `Form.useWatch`) — this is what a static row's value input reads to decide
 * whether the picked property is an enumeration (swap to a `Select` of its
 * options) and what `validateMap` reads to compute inline Save-blocking
 * reasons on every keystroke/row add/remove.
 */
export function HubSpotMappingForm({
  initial,
  properties,
  onSubmit,
  submitting,
  onDirtyChange,
}: HubSpotMappingFormProps) {
  const [form] = Form.useForm<FormShape>();
  const initialValues = toFormShape(initial);
  const [values, setValues] = useState<FormShape>(initialValues);

  useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  const issues = validateMap(values);

  function propertyByName(name: string | undefined): HubSpotContactProperty | undefined {
    if (!name || properties === null) return undefined;
    return properties.find((p) => p.name === name);
  }

  async function handleFinish(formValues: FormShape) {
    const map = toWire(formValues);
    try {
      await onSubmit(map);
      onDirtyChange?.(false);
    } catch {
      // The caller's onSubmit owns its own error messaging — swallow here so
      // a failed save doesn't surface as an unhandled rejection from antd's
      // Form, and, critically, do NOT reset dirty: the operator's edits are
      // still unsaved and should stay guarded (mirrors
      // SalesforceConnectionForm's own handleFinish).
    }
  }

  /**
   * Task 9 — CSV template import's Apply: REPLACES all three sections with
   * the reviewed suggestion set (never merges with whatever was already
   * there) and marks the form dirty, exactly like an operator's own edits
   * would. Two calls are both required, not one:
   *  - `form.setFieldsValue` updates the antd `Form`'s own bound store —
   *    `Form.List`'s array-valued fields are ALWAYS overridden wholesale by
   *    `setFieldsValue` (rc-util's `merge`, "Array will always be
   *    override"), so a shorter/longer reviewed set correctly drops/adds
   *    rows rather than merging by index.
   *  - `setValues` mirrors that same shape into this component's own
   *    `values` state, because `setFieldsValue` does NOT invoke the `Form`'s
   *    `onValuesChange` (only a real field-level `onChange`/dispatch does,
   *    per rc-field-form) — without this, `validateMap`'s inline issues and
   *    the static rows' enum-vs-free-text swap would keep reading the PRE
   *    -import values until the operator's next keystroke.
   * `onDirtyChange?.(true)` is explicit rather than relying on
   * `form.isFieldsTouched()` alone: Apply is documented to mark the form
   * dirty unconditionally (spec — even a reviewed set identical to what was
   * already there counts as an operator action), not only when at least one
   * field's value happens to differ.
   */
  function handleApplyTemplate(suggestions: TemplateSuggestion[]) {
    // The template never carries a description source — keep whatever the
    // operator currently has rather than resetting it to the default.
    const nextValues: FormShape = {
      ...toFormShape(suggestionsToMap(suggestions)),
      descriptionSource: form.getFieldValue('descriptionSource') ?? 'summary',
    };
    form.setFieldsValue(nextValues);
    setValues(nextValues);
    onDirtyChange?.(true);
  }

  return (
    <Form<FormShape>
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={(_changed, allValues) => {
        setValues(allValues);
        onDirtyChange?.(form.isFieldsTouched());
      }}
      onFinish={handleFinish}
    >
      <div style={{ marginBottom: space.lg }}>
        <Space>
          <TemplateImport properties={properties} onApply={handleApplyTemplate} disabled={submitting} />
        </Space>
      </div>

      {properties === null && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: space.lg }}
          message="Couldn't load the portal's HubSpot properties — names below are free text and unvalidated. Run Test Connection after saving to check them against the portal."
        />
      )}

      <SectionHeader>AI summary target</SectionHeader>
      <Form.Item
        name="description"
        label="AI Summary Target"
        validateStatus={issueMessage(issues, 'description') ? 'error' : undefined}
        help={issueMessage(issues, 'description')}
      >
        <PropertyPicker
          properties={properties}
          placeholder={HUBSPOT_SUMMARY_DEFAULT_LABEL}
          allowClear
        />
      </Form.Item>
      <Form.Item
        name="descriptionSource"
        label="Source"
        extra="Full notes writes every note's text, oldest first, separated by |, instead of the AI summary. Very long notes are cut at HubSpot's 65,536-character limit; the full text is still on the contact's timeline. Like the summary, it only fills a blank property: a contact that already has a value keeps it. With no notes, the person's Description is used."
      >
        <Select
          aria-label="Description source"
          options={[
            { value: 'summary', label: 'AI summary' },
            { value: 'notes', label: 'Full notes (voice + typed)' },
          ]}
        />
      </Form.Item>

      <SectionHeader>Static values</SectionHeader>
      <Form.List name="staticRows">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => {
              const row = values.staticRows?.[field.name];
              const property = propertyByName(row?.property);
              const isEnum = !!property?.options && property.options.length > 0;
              return (
                <div
                  key={field.key}
                  data-testid={`static-row-${field.name}`}
                  style={{ display: 'flex', gap: space.sm, alignItems: 'flex-start' }}
                >
                  <Form.Item
                    name={[field.name, 'property']}
                    label="Property"
                    style={{ flex: 1 }}
                    validateStatus={issueMessage(issues, 'static', field.name) ? 'error' : undefined}
                    help={issueMessage(issues, 'static', field.name)}
                  >
                    <PropertyPicker properties={properties} placeholder="Choose a property…" />
                  </Form.Item>
                  <Form.Item name={[field.name, 'value']} label="Value" style={{ flex: 1 }}>
                    {isEnum ? (
                      <Select
                        showSearch
                        placeholder="Choose a value…"
                        options={property!.options!.map((o) => ({ value: o.value, label: o.label }))}
                        optionFilterProp="label"
                      />
                    ) : (
                      <Input placeholder="Value" />
                    )}
                  </Form.Item>
                  <Button
                    type="text"
                    aria-label="Remove"
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(field.name)}
                    style={{ marginTop: 30 }}
                  />
                </div>
              );
            })}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})}>
              Add static value
            </Button>
          </>
        )}
      </Form.List>

      <SectionHeader style={{ marginTop: space.lg }}>Derived bindings</SectionHeader>
      <Form.List name="derivedRows">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <div
                key={field.key}
                data-testid={`derived-row-${field.name}`}
                style={{ display: 'flex', gap: space.sm, alignItems: 'flex-start' }}
              >
                <Form.Item
                  name={[field.name, 'property']}
                  label="Property"
                  style={{ flex: 1 }}
                  validateStatus={issueMessage(issues, 'derived', field.name) ? 'error' : undefined}
                  help={issueMessage(issues, 'derived', field.name)}
                >
                  <PropertyPicker properties={properties} placeholder="Choose a property…" />
                </Form.Item>
                <Form.Item name={[field.name, 'source']} label="Source" style={{ flex: 1 }}>
                  <Select placeholder="Choose a source…" options={DERIVED_SOURCE_OPTIONS} />
                </Form.Item>
                <Button
                  type="text"
                  aria-label="Remove"
                  icon={<MinusCircleOutlined />}
                  onClick={() => remove(field.name)}
                  style={{ marginTop: 30 }}
                />
              </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})}>
              Add derived binding
            </Button>
          </>
        )}
      </Form.List>

      <Form.Item style={{ marginTop: space.lg }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={issues.length > 0}
        >
          Save
        </Button>
      </Form.Item>
    </Form>
  );
}
